import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
} from '@angular/core';
import { CasePipe, CaseType } from '../../../pipes/case.pipe';
import { ToolbarButtonComponent } from '../../core/buttons/toolbar-button.component';

@Component({
  selector: 'app-report-field-tile',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'tw-block tw-h-full tw-min-h-0',
  },
  imports: [ToolbarButtonComponent, CasePipe],
  template: `
    <div
      class="tw-border-2 tw-rounded-lg tw-text-[13px] tw-overflow-hidden tw-h-full tw-flex tw-flex-col"
      [class.tw-w-[120px]]="fixedWidth()"
      [class.tw-min-w-[120px]]="fixedWidth()"
      [class.tw-min-h-[100px]]="fixedWidth()"
      [class.tw-bg-[var(--sed-warm-white-color)]]="!disabled()"
      [class.tw-bg-[var(--sed-open-grey-color)]]="disabled()"
      [class.tw-cursor-grab]="cursorOnTile() && !disabled()"
      [class.tw-cursor-not-allowed]="cursorOnTile() && disabled()"
    >
      <div class="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
        <div class="tw-flex tw-shrink-0 tw-justify-between tw-w-full">
          <div>
            @if (showLockIcon() && disabled()) {
              <app-toolbar-button
                icon="fa-light fa-lock"
                type="Small"
                [disabled]="true"
                ariaLabel="Field is locked"
              />
            }
            @if (showEditButton() && !disabled()) {
              <app-toolbar-button
                icon="fa-light fa-edit"
                type="Small"
                (click)="edit.emit()"
                [ariaLabel]="'Edit ' + label()"
              />
            }
          </div>
          <div>
            @if (hasField() && showClearButton() && !disabled()) {
              <app-toolbar-button
                icon="fa-light fa-xmark"
                type="Small"
                (click)="clear.emit()"
                [ariaLabel]="'Clear ' + label()"
              />
            }
          </div>
        </div>
        <div
          class="tw-px-3 tw-font-bold tw-py-2 tw-min-h-0 tw-flex-1 tw-text-[var(--sed-dynamic-black-color)]"
          [style.min-height]="minHeight()"
          [class.tw-break-words]="wrapLabel()"
          [class.tw-overflow-hidden]="wrapLabel()"
          [class.tw-text-center]="!hasField()"
        >
          {{ label() | case: labelCase() }}
        </div>
      </div>
    </div>
  `,
})
export class ReportFieldTileComponent {
  /** The display text (selected field name or default/empty label). */
  label = input.required<string>();

  labelCase = input<CaseType>('none');

  /** Whether a field is selected (controls X visibility and label styling). */
  hasField = input<boolean>(false);

  /** Locked/disabled state (grey bg, lock icon, not-allowed cursor). */
  disabled = input<boolean>(false);

  /** Whether to show the edit button (default true). */
  showEditButton = input<boolean>(true);

  /** Whether to show the X/clear button when a field is selected (default true). */
  showClearButton = input<boolean>(true);

  /** Whether to show the lock icon when disabled (default false). */
  showLockIcon = input<boolean>(false);

  /** If true, applies fixed 120px width; if false, tile grows with its container. */
  fixedWidth = input<boolean>(false);

  /** Configurable label area min-height (e.g. '40px' or '56px'). */
  minHeight = input<string>('40px');

  /** If true, wraps long labels and stretches to match other tiles in the grid row. */
  wrapLabel = input<boolean>(false);

  /** If true, applies grab/not-allowed cursor on the tile itself. */
  cursorOnTile = input<boolean>(false);

  /** Emitted when the edit button is clicked. */
  edit = output<void>();

  /** Emitted when the X/clear button is clicked. */
  clear = output<void>();
}
